import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

/**
 * Shared warm-toned shell for the /setup/* mini-pages that make up the
 * First Light onboarding walkthrough. Each host page renders its own
 * "one focused field" as ``children``; this shell handles the progress
 * dots, editorial title, "Save & continue tour" button, and the
 * "Not now — skip this step" escape hatch. Fully mobile-responsive
 * (single column ≤sm, button row that never overflows).
 *
 * Props:
 *   step             — 1|2|3 (out of 3 setup pages)
 *   eyebrow          — short caption above the title, e.g. "STEP 1 OF 3"
 *   title            — H1 headline
 *   subtitle         — softer explanatory paragraph
 *   children         — the focused field (cuisine picker, staples preset, etc.)
 *   ctaLabel         — primary button label (default "Save & continue")
 *   onSaveContinue   — async callback fired on primary tap. Should throw to
 *                      cancel navigation. Resolving means "advance the tour".
 *   nextRoute        — where to navigate on success (last page uses "/" and
 *                      the onSaveContinue callback marks the tour complete)
 *   busy             — parent-controlled loading state for the primary CTA
 *   skipLabel        — text for the escape link (default "Skip this step")
 */
export default function SetupStep({
  step,
  eyebrow,
  title,
  subtitle,
  children,
  ctaLabel = "Save & continue",
  onSaveContinue,
  nextRoute,
  busy = false,
  skipLabel = "Skip this step",
}) {
  const nav = useNavigate();
  const { updateOnboarding } = useAuth();
  const [localBusy, setLocalBusy] = React.useState(false);
  const isRunning = busy || localBusy;

  const handleContinue = async () => {
    if (isRunning) return;
    setLocalBusy(true);
    try {
      if (onSaveContinue) await onSaveContinue();
      const stepIndex = step + 1;  // /setup pages map to tour steps 2..5
      const isLast = step === 3;
      // Await the onboarding patch so the AuthContext ``user`` state
      // reflects the new onboarding_step / completed flag BEFORE we
      // navigate — otherwise the OnboardingTour auto-opens on the
      // destination page at the stale step.
      try {
        await updateOnboarding(
          isLast ? { completed: true } : { step: stepIndex + 1 }
        );
      } catch {
        // Server sync failed — still advance the user client-side.
      }
      nav(nextRoute);
    } catch (err) {
      // onSaveContinue signalled cancel — leave the user on this page.
    } finally {
      setLocalBusy(false);
    }
  };

  const handleSkip = async () => {
    if (isRunning) return;
    setLocalBusy(true);
    try {
      const stepIndex = step + 1;
      const isLast = step === 3;
      try {
        await updateOnboarding(
          isLast ? { completed: true } : { step: stepIndex + 1 }
        );
      } catch { /* non-blocking */ }
      nav(nextRoute);
    } finally {
      setLocalBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-hearth-cream text-hearth-ink">
      {/* Top glow strip — matches the tour modal for continuity */}
      <div
        className="h-1.5"
        style={{
          background:
            "linear-gradient(90deg, #B45A37 0%, #d68a5a 45%, #B45A37 100%)",
        }}
      />

      <div className="max-w-3xl mx-auto px-6 sm:px-8 pt-8 pb-16">
        {/* Top bar — logo + back to dashboard */}
        <div className="flex items-center justify-between mb-8">
          <Logo size={30} />
          <Link
            to="/"
            data-testid="setup-back-home"
            className="text-xs text-hearth-mute hover:text-hearth-ink inline-flex items-center gap-1"
          >
            <ArrowLeft size={12} /> Back to house
          </Link>
        </div>

        {/* Progress dots — 3 setup pages */}
        <div className="flex items-center gap-2 mb-8" role="tablist" aria-label="Setup progress">
          {[1, 2, 3].map((i) => {
            const active = i === step;
            const done = i < step;
            return (
              <span
                key={i}
                data-testid={`setup-dot-${i}`}
                className={`rounded-full transition-all ${
                  active ? "w-8 h-1.5 bg-hearth-clay"
                    : done ? "w-1.5 h-1.5 bg-hearth-clay/50"
                    : "w-1.5 h-1.5 bg-hearth-line"
                }`}
                aria-hidden
              />
            );
          })}
        </div>

        <div className="caption text-hearth-clayDark tracking-widest mb-2">
          {eyebrow}
        </div>
        <h1
          data-testid="setup-title"
          className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-hearth-ink leading-tight"
        >
          {title}
        </h1>
        {subtitle && (
          <p className="mt-3 text-hearth-mute max-w-xl leading-relaxed">
            {subtitle}
          </p>
        )}

        {/* Focused field */}
        <div className="mt-10 rounded-3xl border border-hearth-line/70 bg-white p-6 sm:p-8 shadow-sm">
          {children}
        </div>

        {/* Actions — stacked on mobile so the primary button is thumb-first */}
        <div className="mt-8 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
          <button
            data-testid="setup-skip-btn"
            type="button"
            onClick={handleSkip}
            disabled={isRunning}
            className="text-sm text-hearth-mute hover:text-hearth-ink italic transition disabled:opacity-50"
          >
            {skipLabel}
          </button>
          <Button
            data-testid="setup-continue-btn"
            type="button"
            onClick={handleContinue}
            disabled={isRunning}
            className="h-12 rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white font-medium px-8 w-full sm:w-auto min-w-[220px]"
          >
            {isRunning ? (
              <>
                <Loader2 className="animate-spin mr-2" size={16} /> Saving…
              </>
            ) : (
              <>
                {ctaLabel} <ArrowRight size={16} className="ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
